import { errorRowsToList, warningRowsToList } from "@/features/people/lib/dry-run-summary";
import type { DryRunResult } from "@/features/people/schemas/import.schema";

type DryRunRow = DryRunResult["rows"][number];

interface RowTableProps {
  rows: DryRunResult["rows"];
  hidden: number;
  intro: string;
  detailHeader: string;
  detail: (row: DryRunRow) => string;
  hiddenNoun: string;
}

/// File row numbers count the header as row 1, so a reader can find the line
/// in the spreadsheet they exported.
function DryRunRowTable({ rows, hidden, intro, detailHeader, detail, hiddenNoun }: RowTableProps) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-text-secondary">{intro}</p>
      <div className="max-h-64 overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted text-xs uppercase tracking-wide text-text-tertiary">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                Row
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                Email
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {detailHeader}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rowIndex} className="border-t border-border align-top">
                <td className="px-3 py-2 tabular-nums text-text-tertiary">{row.rowIndex + 2}</td>
                <td
                  className="max-w-48 truncate px-3 py-2 font-mono text-xs"
                  title={row.email ?? ""}
                >
                  {row.email ?? "—"}
                </td>
                <td className="px-3 py-2 text-text-secondary">{detail(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hidden > 0 ? (
        <p className="text-xs text-text-tertiary">
          And {hidden} more row{hidden === 1 ? "" : "s"} {hiddenNoun}.
        </p>
      ) : null}
    </div>
  );
}

/// The rows the commit will skip, each with the shore's reason.
export function DryRunErrorRows({ dry }: { dry: DryRunResult }) {
  const { rows, hidden } = errorRowsToList(dry);
  return (
    <DryRunRowTable
      rows={rows}
      hidden={hidden}
      intro="These rows will be skipped. Fix them in your file and import it again to bring them in."
      detailHeader="Reason"
      detail={(row) => row.errorReason ?? "This row could not be read."}
      hiddenNoun="with errors"
    />
  );
}

/// People who will be imported, minus the fields the shore could not accept.
/// The warning text is value-free by contract, so it never echoes a cell.
export function DryRunWarningRows({ dry }: { dry: DryRunResult }) {
  const { rows, hidden } = warningRowsToList(dry);
  const total = rows.length + hidden;
  return (
    <DryRunRowTable
      rows={rows}
      hidden={hidden}
      intro={`${total} ${total === 1 ? "person" : "people"} will be imported with some fields skipped. You can fill those in later from their profile.`}
      detailHeader="Skipped fields"
      detail={(row) => (row.warnings ?? []).join("; ")}
      hiddenNoun="with skipped fields"
    />
  );
}
